import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { connect } from 'react-redux';
import ApiService from '../../../../services/apiService';
import CurrencyGrid from './currencyGrid';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { typeOptions } from '../../../../../Enums/config';
import { FiArrowLeft, FiPlus, FiSave, FiX } from 'react-icons/fi';

const emptyForm = {
  id: undefined,
  name: '',
  code: '',
  conversionRate: '',
  default: false,
  loggedInUserId: '',
  currencyDenomination: [{ value: '', type: 0 }],
};

const AddCurrency = ({ Currency }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currencies, setCurrencies] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);

  // Helpers: detect Coin/Note types by option label (case-insensitive)
  const getTypeOptionByType = (t) => typeOptions.find((o) => o.type === t);
  const isCoinOrNote = (t) => {
    const opt = getTypeOptionByType(t);
    const lbl = (opt?.label || opt?.value || '').toString();
    return /coin|note/i.test(lbl);
  };

  // --------- Lifecycle
  useEffect(() => {
    const editCurrency = location.state || null;
    if (editCurrency && editCurrency.id) startEdit(editCurrency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      setLoadingList(true);
      const api = new ApiService();
      const res = await api.get('GET_CURRENCY');
      setCurrencies(res?.data?.result || []);
    } catch (e) {
      console.error(e);
      toast.error('Error fetching currencies');
    } finally {
      setLoadingList(false);
    }
  };

  const startEdit = (currency) => {
    setFormData({
      ...currency,
      currencyDenomination:
        currency.currencyDenomination && currency.currencyDenomination.length
          ? currency.currencyDenomination.map((d) => ({
              value: d?.value === 0 ? '' : d?.value, // normalize zero to empty for validation UI
              type: d?.type ?? 0,
            }))
          : [{ value: '', type: 0 }],
    });
    setIsEditing(true);
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setIsEditing(false);
  };

  // ---------- Sanitizers
  const sanitizeConversionRate = (val) => {
    if (val === '') return '';
    const n = Number(val);
    if (!isFinite(n)) return '';
    // Must be > 0.00
    return n > 0 ? n : '';
  };

  const sanitizeDenomination = (val, typeId) => {
    if (val === '') return '';
    const raw = String(val);

    if (isCoinOrNote(typeId)) {
      // integer >= 1
      const n = parseInt(raw, 10);
      return isFinite(n) && n >= 1 ? n : '';
    } else {
      // decimal > 0.00
      const n = Number(raw);
      return isFinite(n) && n > 0 ? n : '';
    }
  };

  // ---------- Handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'code') {
      setFormData((p) => ({ ...p, code: value.toUpperCase().slice(0, 3) }));
      return;
    }

    if (name === 'conversionRate') {
      setFormData((p) => ({ ...p, conversionRate: sanitizeConversionRate(value) }));
      return;
    }

    if (type === 'checkbox') {
      setFormData((p) => ({ ...p, [name]: checked }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleDenominationChange = (idx, e) => {
    const { value } = e.target;
    setFormData((p) => {
      const list = [...p.currencyDenomination];
      const t = list[idx]?.type ?? 0;
      list[idx].value = sanitizeDenomination(value, t);
      return { ...p, currencyDenomination: list };
    });
  };

  const handleTypeChange = (idx, opt) => {
    setFormData((p) => {
      const list = [...p.currencyDenomination];
      const prevType = list[idx]?.type ?? 0;
      const nextType = opt?.type ?? 0;
      list[idx].type = nextType;

      // Re-sanitize existing value when type changes (e.g., decimal -> integer)
      list[idx].value = sanitizeDenomination(list[idx].value, nextType);
      return { ...p, currencyDenomination: list };
    });
  };

  const addRow = () => {
    setFormData((p) => ({
      ...p,
      currencyDenomination: [...p.currencyDenomination, { value: '', type: 0 }],
    }));
  };

  const removeRow = (idx) => {
    setFormData((p) => {
      const list = [...p.currencyDenomination];
      list.splice(idx, 1);
      return { ...p, currencyDenomination: list.length ? list : [{ value: '', type: 0 }] };
    });
  };

  // ---------- Validation
  const errors = useMemo(() => {
    const e = {};
    if (!formData.name?.trim()) e.name = 'Name is required';
    if (!formData.code?.trim()) e.code = 'Code is required';
    if (formData.code && formData.code.length !== 3) e.code = 'Use 3-letter code (e.g., USD)';

    // Conversion rate must be decimal > 0.00
    if (formData.conversionRate === '' || isNaN(formData.conversionRate)) {
      e.conversionRate = 'Conversion rate is required';
    } else if (!(Number(formData.conversionRate) > 0)) {
      e.conversionRate = 'Conversion rate must be greater than 0.00';
    }

    // Denominations: Coin/Note = integer >=1 ; others decimal > 0.00
    const denErrors = [];
    (formData.currencyDenomination || []).forEach((d, idx) => {
      const t = d?.type ?? 0;
      const val = d?.value;

      if (val === '' || val === null || typeof val === 'undefined') {
        denErrors[idx] = 'Required';
        return;
      }

      if (isCoinOrNote(t)) {
        if (!Number.isInteger(Number(val)) || Number(val) < 1) {
          denErrors[idx] = 'Must be an integer ≥ 1';
        }
      } else {
        if (!(Number(val) > 0)) {
          denErrors[idx] = 'Must be greater than 0.00';
        }
      }
    });

    if (denErrors.some(Boolean)) e.currencyDenomination = denErrors;
    return e;
  }, [formData]);

  const isValid = useMemo(() => {
    if (Object.keys(errors).length === 0) return true;
    // Allow parent-level errors only for denominations array if all entries are valid
    if (errors.currencyDenomination && Array.isArray(errors.currencyDenomination)) {
      return !errors.name && !errors.code && !errors.conversionRate && !errors.currencyDenomination.some(Boolean);
    }
    return false;
  }, [errors]);

  const handleSave = async () => {
    // Defense in depth: block if any denomination invalid
    if (!isValid) {
      toast.warning('Please fix the highlighted fields');
      return;
    }

    setSaving(true);
    try {
      const api = new ApiService();
      const payload = {
        ...formData,
        code: formData.code?.toUpperCase(),
        // Ensure sanitized denominations are sent
        currencyDenomination: (formData.currencyDenomination || []).map((d) => ({
          value: isCoinOrNote(d.type) ? parseInt(d.value, 10) : Number(d.value),
          type: d.type,
        })),
      };

      if (isEditing) {
        const res = await api.put('UPDATE_CURRENCY', payload);
        if (res?.status === 200) toast.success(res?.data?.message || 'Currency updated');
      } else {
        const res = await api.post('CREATE_CURRENCY', payload);
        if (res?.status === 201 || res?.status === 200) toast.success(res?.data?.message || 'Currency created');
      }

      await loadCurrencies();
      resetForm();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save currency');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 m-0">
      {/* Header */}
      <button onClick={() => navigate(-1)} className="addItem-btn mb-3" title="Back">
        <FiArrowLeft /> Back
      </button>
      <div
        className="d-flex justify-content-between align-items-center mb-4"
        style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 10, padding: '14px 16px' }}
      >
        <div className="d-flex align-items-center gap-2">
          <h4 style={{ margin: 0, color: '#495057', fontWeight: 600 }}>{isEditing ? 'Editing Currency' : 'Create Currency'}</h4>
          {/* <span
            className="badge"
            style={{
              background: isEditing ? '#ffeaa7' : '#E8E2FF',
              color: isEditing ? '#856404' : '#6E5199',
              marginLeft: 8,
              fontWeight: 700,
            }}
          >
            {isEditing ? 'EDIT MODE' : 'CREATE MODE'}
          </span> */}
        </div>

        <div className="d-flex gap-2">
          {isEditing && (
            <button className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
              <FiX /> Cancel Edit
            </button>
          )}
          <button className="btn btn-sm" onClick={resetForm} style={{ background: '#f8f9fa', borderColor: '#ced4da', color: '#495057' }} title="New currency">
            <FiPlus /> New Currency
          </button>
          <button
            className="btn btn-sm"
            onClick={handleSave}
            disabled={!isValid || saving}
            style={{ background: isValid ? '#6E5199' : '#adb5bd', color: '#fff', borderColor: 'transparent' }}
          >
            <FiSave /> {isEditing ? 'Update' : 'Save'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="catlist">
        <div className="row g-4">
          <div className="col-lg-5">
            <label className="form-label">Currency Name</label>
            <input
              type="text"
              className={`form-control ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., US Dollar"
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="col-lg-1" />

          <div className="col-lg-5">
            <label className="form-label">Currency Code</label>
            <input
              type="text"
              className={`form-control ${errors.code ? 'is-invalid' : ''}`}
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="USD"
              maxLength={3}
              style={{ textTransform: 'uppercase' }}
            />
            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
          </div>

          <div className="col-lg-5">
            <label className="form-label">Conversion Rate</label>
            <input
              type="number"
              className={`form-control ${errors.conversionRate ? 'is-invalid' : ''}`}
              name="conversionRate"
              value={formData.conversionRate}
              onChange={handleInputChange}
              placeholder="e.g., 1.00, 277.3500"
              min="0.01"
              step="0.0001"
            />
            {errors.conversionRate && <div className="invalid-feedback">{errors.conversionRate}</div>}
          </div>

          <div className="col-lg-1" />

          <div className="col-lg-5 d-flex align-items-end">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="defaultCurrency"
                name="default"
                checked={!!formData.default}
                onChange={handleInputChange}
              />
              <label htmlFor="defaultCurrency" className="form-check-label">
                Set as Default
              </label>
            </div>
          </div>
        </div>

        {/* Denominations */}
        <div className="row mt-4 mb-2">
          <h5 style={{ fontWeight: 600, color: '#495057' }}>Denominations</h5>

          {(formData.currencyDenomination || []).map((den, idx) => {
            const rowError = Array.isArray(errors.currencyDenomination) ? errors.currencyDenomination[idx] : undefined;
            const coinOrNote = isCoinOrNote(den.type);

            return (
              <div key={idx} className="row g-3 mt-1">
                <div className="col-lg-5">
                  <label className="form-label">Value </label>
                  <input
                    type="number"
                    className={`form-control ${rowError ? 'is-invalid' : ''}`}
                    value={den.value}
                    min={coinOrNote ? 1 : 0.01}
                    step={coinOrNote ? 1 : 0.01}
                    onChange={(e) => handleDenominationChange(idx, e)}
                    placeholder={coinOrNote ? 'e.g., 1, 2, 5, 10' : 'e.g., 0.25, 2.50'}
                  />

                  <div style={{ minHeight: '1.25rem' }}>{rowError && <div className="invalid-feedback d-block">{rowError}</div>}</div>
                </div>

                <div className="col-lg-1" />

                <div className="col-lg-5">
                  <label className="form-label">Type</label>
                  <Select
                    value={getTypeOptionByType(den.type)}
                    onChange={(opt) => handleTypeChange(idx, opt)}
                    options={typeOptions}
                    placeholder="Select type"
                    styles={{
                      control: (p) => ({ ...p, minHeight: 42 }),
                      menu: (p) => ({ ...p, zIndex: 5 }),
                    }}
                  />
                </div>

                <div className="col-lg-1 d-flex justify-content-center align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => removeRow(idx)}
                    disabled={(formData.currencyDenomination || []).length <= 1}
                  >
                    <FiX /> Remove
                  </button>
                </div>
              </div>
            );
          })}

          <div className="col-12 mt-3">
            <button className="btn btn-outline-primary" onClick={addRow}>
              <FiPlus /> Add Denomination
            </button>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="mt-4">
        <CurrencyGrid data={currencies} loading={loadingList} onEditCurrency={startEdit} onDeleted={loadCurrencies} />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;
export default connect(mapStateToProps)(AddCurrency);
